import { Conditions, Fields } from '@root/constants'
import { IAccountingRule, Rule } from '@root/query'
import React, { useMemo, useState } from 'react'
import { useFormContext, type UseFieldArrayAppend, type UseFieldArrayRemove } from 'react-hook-form'
import { FaEuroSign, FaTimes } from 'react-icons/fa'
import {
  AmountPrefix,
  AmountSuffix,
  AmountValue,
  AmountWell,
  AmountWells,
  AndMark,
  ConditionLabel,
  ConditionLabelText,
  ConditionRow,
  ConditionTitle,
  RangeDash,
  RemoveButton
} from './AccountingRuleForm.styled'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface AmountRange {
  min: string
  max: string
}

const COMPLETE_AMOUNT = /^-?\d+(\.\d+)?$/

const parseAmountInput = (raw: string): number | null => {
  const normalized = raw.trim().replaceAll(',', '.')
  if (!normalized) return null
  if (!COMPLETE_AMOUNT.test(normalized)) return Number.NaN
  return Number(normalized)
}

const amountRangeHasInvalidNumber = (range: AmountRange) =>
  Number.isNaN(parseAmountInput(range.min)) || Number.isNaN(parseAmountInput(range.max))

export const getAmountRangeFromRules = (rules?: Array<Rule | any>): AmountRange => {
  if (!rules) return { min: '', max: '' }

  let min = ''
  let max = ''

  for (const rule of rules) {
    if (!('field' in rule) || rule.field !== Fields.MONEY) continue

    if (rule.condition === Conditions.GREATER_THAN_EQUAL) {
      min = String(rule.value)
    } else if (rule.condition === Conditions.LESS_THAN_EQUAL) {
      max = String(rule.value)
    } else if (rule.condition === Conditions.EQUALS) {
      min = String(rule.value)
      max = String(rule.value)
    }
  }

  return { min, max }
}

const moneyRulesMatch = (current: Rule[], next: Rule[]) =>
  current.length === next.length &&
  current.every(
    (rule, index) =>
      rule.field === next[index].field &&
      rule.condition === next[index].condition &&
      rule.value === next[index].value
  )

export const buildMoneyRulesFromRange = (range: AmountRange): Rule[] => {
  const minNum = parseAmountInput(range.min)
  const maxNum = parseAmountInput(range.max)
  const min = Number.isNaN(minNum) ? null : minNum
  const max = Number.isNaN(maxNum) ? null : maxNum

  if (min === null && max === null) return []

  if (min !== null && max !== null && min === max) {
    return [{ field: Fields.MONEY, condition: Conditions.EQUALS, value: min }]
  }

  const rules: Rule[] = []
  if (min !== null) {
    rules.push({ field: Fields.MONEY, condition: Conditions.GREATER_THAN_EQUAL, value: min })
  }
  if (max !== null) {
    rules.push({ field: Fields.MONEY, condition: Conditions.LESS_THAN_EQUAL, value: max })
  }
  return rules
}

interface AmountFilterProps {
  showAnd: boolean
  onClose: () => void
  append: UseFieldArrayAppend<IAccountingRule, 'rule_node.rules'>
  remove: UseFieldArrayRemove
}

const AmountFilter: React.FC<AmountFilterProps> = ({ showAnd, onClose, append, remove }) => {
  const [t] = useTranslation()
  const { getValues, setError, clearErrors, getFieldState } = useFormContext<IAccountingRule>()

  const initialRange = useMemo(() => getAmountRangeFromRules(getValues('rule_node.rules')), [])
  const [range, setRange] = useState<AmountRange>(initialRange)
  const amountInvalid = amountRangeHasInvalidNumber(range)

  const commitRange = (next: AmountRange) => {
    const currentRules = getValues('rule_node.rules') || []
    const moneyIndexes: number[] = []
    const currentMoneyRules: Rule[] = []
    currentRules.forEach((rule, index) => {
      if ('field' in rule && rule.field === Fields.MONEY) {
        moneyIndexes.push(index)
        currentMoneyRules.push(rule)
      }
    })
    const moneyRules = buildMoneyRulesFromRange(next)
    // replace() / setValue() on this array gives every row a new id, so the contact
    // field remounts and its search input takes the next keystroke.
    if (!moneyRulesMatch(currentMoneyRules, moneyRules)) {
      if (moneyIndexes.length > 0) remove(moneyIndexes)
      if (moneyRules.length > 0) append(moneyRules, { shouldFocus: false })
    }
    if (amountRangeHasInvalidNumber(next)) {
      setError('rule_node', { type: 'amount', message: t('validation.invalidNumber') })
    } else if (getFieldState('rule_node').error?.type === 'amount') {
      clearErrors('rule_node')
    }
  }

  const handleClose = () => {
    commitRange({ min: '', max: '' })
    onClose()
  }

  return (
    <ConditionRow $divider={showAnd}>
      <ConditionLabel>
        <FaEuroSign />
        <ConditionLabelText>
          {showAnd && (
            <AndMark>{t('settings.automation.accountingRules.form.conditionAnd')}</AndMark>
          )}
          <ConditionTitle>
            {t('settings.automation.accountingRules.form.conditionAmount')}
          </ConditionTitle>
        </ConditionLabelText>
      </ConditionLabel>
      <AmountFields>
        <AmountWells>
          <AmountWell>
            <AmountPrefix>
              {t('settings.automation.accountingRules.form.money.minLabel')}
            </AmountPrefix>
            <AmountValue
              inputMode="decimal"
              autoComplete="off"
              value={range.min}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next = { ...range, min: e.target.value }
                setRange(next)
                commitRange(next)
              }}
            />
            <AmountSuffix>€</AmountSuffix>
          </AmountWell>
          <RangeDash>–</RangeDash>
          <AmountWell>
            <AmountPrefix>
              {t('settings.automation.accountingRules.form.money.maxLabel')}
            </AmountPrefix>
            <AmountValue
              inputMode="decimal"
              autoComplete="off"
              value={range.max}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next = { ...range, max: e.target.value }
                setRange(next)
                commitRange(next)
              }}
            />
            <AmountSuffix>€</AmountSuffix>
          </AmountWell>
        </AmountWells>
        {amountInvalid && <AmountError>{t('validation.invalidNumber')}</AmountError>}
      </AmountFields>
      <RemoveButton
        type="button"
        onClick={handleClose}
        aria-label={t('settings.automation.accountingRules.form.money.remove')}
      >
        <FaTimes />
      </RemoveButton>
    </ConditionRow>
  )
}

export default AmountFilter

const AmountFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const AmountError = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.error};
`
